/**
 * MeetingRowActions — the per-row action cluster for a meeting: the one primary button the
 * row's state earns, plus the overflow menu behind it.
 *
 * Extracted from `MeetingsList` (826 lines). Three consecutive deep-review passes flagged that
 * file as a shotgun-surgery risk and each pass then added to it — TASK-673 added the capture
 * chooser, 676 the bot dispatch, 677 reschedule/cancel — because every row action had to be
 * edited in the middle of the same JSX tree. This is the seam those tickets kept crossing.
 *
 * Deliberately presentational: it decides which actions a row EARNS, and calls back for what
 * they do. Modal state, refetching and toasts stay in the list, which owns them.
 */

import { MoreHorizontal } from 'lucide-react';

import type { MeetingListItem } from '../../services/api/meetingApi';

export interface MeetingRowActionsProps {
  meeting: MeetingListItem;
  /** The row is scheduled and has not happened yet — it can be started, moved or called off. */
  startable: boolean;
  /** The meeting is running now. */
  live: boolean;
  /** It is over and has something to review. */
  reviewable: boolean;
  /** It is over and needs a dictated summary — takes precedence over Review. */
  dictate: boolean;
  /** Session id whose bot dispatch is in flight, for the busy label. */
  dispatchingBot: string | null;
  /** Session id whose overflow menu is open (`null` = all closed). */
  openMenuSessionId: string | null;
  onToggleMenu: (sessionId: string) => void;
  onCloseMenu: () => void;
  onStart: (meeting: MeetingListItem) => void;
  onOpen: (meeting: MeetingListItem) => void;
  onDictate: (meeting: MeetingListItem) => void;
  onReschedule: (meeting: MeetingListItem) => void;
  onCancel: (meeting: MeetingListItem) => void;
  onSendBot: (meeting: MeetingListItem) => void;
}

const PRIMARY =
  'rounded-lg bg-kielis-navy px-3 py-2 text-sm font-medium text-white hover:bg-kielis-navy/90';
const MENU_ITEM = 'block w-full rounded px-3 py-2 text-left text-sm hover:bg-muted';

export function MeetingRowActions({
  meeting: m,
  startable,
  live,
  reviewable,
  dictate,
  dispatchingBot,
  openMenuSessionId,
  onToggleMenu,
  onCloseMenu,
  onStart,
  onOpen,
  onDictate,
  onReschedule,
  onCancel,
  onSendBot,
}: MeetingRowActionsProps) {
  const menuOpen = openMenuSessionId === m.session_id;
  // `dictate` wins over `reviewable`: an undocumented meeting needs its summary before there is
  // anything worth reviewing, so offering both would send the RM to the emptier of the two.
  const withMenuClosed = (run: () => void) => () => {
    onCloseMenu();
    run();
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      {startable && (
        <button type="button" data-testid="start-action" onClick={() => onStart(m)} className={PRIMARY}>
          Start meeting
        </button>
      )}
      {live && (
        <button type="button" data-testid="open-live-action" onClick={() => onOpen(m)} className={PRIMARY}>
          Open live meeting
        </button>
      )}
      {reviewable && !dictate && (
        <button type="button" data-testid="review-action" onClick={() => onOpen(m)} className={PRIMARY}>
          Review meeting
        </button>
      )}
      {dictate && (
        <button type="button" data-testid="dictate-action" onClick={() => onDictate(m)} className={PRIMARY}>
          Dictate summary
        </button>
      )}

      <div className="relative">
        <button
          type="button"
          aria-label={`More actions for ${m.client_name}${m.meeting_goal ? ` — ${m.meeting_goal}` : ''}`}
          aria-expanded={menuOpen}
          onClick={() => onToggleMenu(m.session_id)}
          className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-11 z-20 min-w-44 rounded-lg border border-border bg-background p-1 shadow-lg"
          >
            {startable ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="reschedule-action"
                  onClick={withMenuClosed(() => onReschedule(m))}
                  className={MENU_ITEM}
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="cancel-action"
                  onClick={withMenuClosed(() => onCancel(m))}
                  className="block w-full rounded px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  Cancel meeting
                </button>
                {m.meeting_url && !m.has_bot && (
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="send-bot-action"
                    disabled={dispatchingBot === m.session_id}
                    onClick={withMenuClosed(() => onSendBot(m))}
                    className={`${MENU_ITEM} disabled:opacity-50`}
                  >
                    {dispatchingBot === m.session_id ? 'Sending bot…' : 'Send recording bot'}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={withMenuClosed(() => onOpen(m))}
                className={MENU_ITEM}
              >
                Open details
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MeetingRowActions;
